/**
 * What you hand to whoever owns the sending system.
 *
 * Registering a type nothing sends yet is only half a job: the other half is
 * the endpoint, the body and a key that may post it. This is the old trigger
 * picker's "you'll get a reference to pass on", actually delivered.
 *
 * Until the first event arrives the type is inert, and the panel says so
 * rather than looking finished.
 */
import { useState } from 'react';
import { Badge, Button, Card, IconButton } from '@servicecompressionllc/sc-design-system';
import { Clock, Copy, Check, KeyRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiOrigin } from '../../lib/apiClient.js';
import type { ApiKey, TenantEventType } from '../../lib/types.js';

interface Props {
  entry: TenantEventType;
  /** Keys that may post events — the handoff names one rather than sending
   *  the reader off to guess which. */
  emitKeys: ApiKey[];
  /** Sends one synthetic event of this type through the ordinary ingest path. */
  onSendTest: () => Promise<unknown>;
  isBusy: boolean;
}

export const EventHandoff = ({ entry, emitKeys, onSendTest, isBusy }: Props) => {
  const [copied, setCopied] = useState<'url' | 'curl' | null>(null);
  const [tested, setTested] = useState(false);
  const url = `${apiOrigin}/api/events`;
  const seen = entry.receivedCount > 0;

  /** Runnable as pasted, which the displayed body is not: it carries a JSON
   *  comment and a prose `dedupeKey`. dedupeKey is left out on purpose — an
   *  integrator pasting the placeholder would dedupe every send against it. */
  const curl = `curl -X POST ${url} \\
  -H "Authorization: Bearer $SC_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"${entry.type}","payload":{}}'`;

  const copy = (what: 'url' | 'curl') => {
    void navigator.clipboard.writeText(what === 'url' ? url : curl).then(() => {
      setCopied(what);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className="flex flex-col gap-5">
      <div
        className={`flex items-center gap-3 rounded-lg border p-3 ${
          seen ? 'border-success-border bg-success-soft' : 'border-info-border bg-info-soft'
        }`}
      >
        {seen ? (
          <Check className="text-success h-4 w-4 flex-shrink-0" />
        ) : (
          <Clock className="text-info h-4 w-4 flex-shrink-0" />
        )}
        <div className="min-w-0 flex-grow">
          <p className={`text-xs font-semibold ${seen ? 'text-success' : 'text-info'}`}>
            {seen ? `${entry.receivedCount} received` : 'Waiting for the first event'}
          </p>
          <p className="text-ink-subtle mt-0.5 text-xs">
            {seen
              ? 'The fields are read from what actually arrived.'
              : 'This panel updates the moment one arrives, and you’ll confirm the fields then.'}
          </p>
        </div>
      </div>

      <div>
        <h4 className="text-ink-muted text-xs font-semibold uppercase tracking-wide">
          Send it here
        </h4>
        <p className="text-ink-subtle mt-1 text-xs">
          Hand this to whoever owns the sending system.
        </p>
        <Card padding="none" className="mt-2.5 overflow-hidden">
          <div className="border-outline bg-surface flex items-center gap-2.5 border-b px-3 py-2.5">
            <Badge variant="success">POST</Badge>
            <span className="text-ink min-w-0 flex-grow truncate font-mono text-xs">{url}</span>
            <IconButton aria-label="Copy the URL" size="sm" onClick={() => copy('url')}>
              {copied === 'url' ? <Check /> : <Copy />}
            </IconButton>
          </div>
          <pre className="bg-surface text-ink-muted overflow-x-auto p-3 font-mono text-xs leading-[18px]">
            {'Authorization: Bearer '}
            <span className="text-ink-subtle">{'<your API key>'}</span>
            {'\nContent-Type: application/json\n\n{\n  '}
            <span className="text-info">{'"type"'}</span>
            {': '}
            <span className="text-success">{`"${entry.type}"`}</span>
            {',\n  '}
            <span className="text-info">{'"payload"'}</span>
            {': { '}
            <span className="text-ink-subtle">{'/* your fields */'}</span>
            {' },\n  '}
            <span className="text-info">{'"dedupeKey"'}</span>
            {': '}
            <span className="text-ink-subtle">{'"optional. Redeliveries are no-ops"'}</span>
            {'\n}'}
          </pre>
          <div className="border-outline bg-surface flex items-center justify-end border-t px-3 py-2">
            <Button variant="secondary" size="sm" onClick={() => copy('curl')}>
              {copied === 'curl' ? 'Copied' : 'Copy as cURL'}
            </Button>
          </div>
        </Card>
        {/* Naming the keys was a list nobody could act on: the secret is
            returned only when a key is created, so this panel can never hand
            one over. The ABSENCE is the actionable half. */}
        {emitKeys.length > 0 ? (
          <p className="text-ink-subtle mt-2 text-xs">
            Any enabled key holding <span className="font-mono">Events.Emit</span> can post this.
            The catalog decides which types are accepted, not which key sent them.
          </p>
        ) : (
          <div className="border-outline bg-surface mt-2.5 flex items-center gap-3 rounded-lg border px-3 py-2.5">
            <KeyRound className="text-ink-muted h-4 w-4 flex-shrink-0" />
            <p className="text-ink-muted min-w-0 flex-grow text-xs">
              No enabled key holds <span className="font-mono">Events.Emit</span>, so nothing can
              send this yet.
            </p>
            <Link
              to="/settings/api-keys"
              className="text-info flex-shrink-0 text-xs font-semibold hover:underline"
            >
              Manage keys →
            </Link>
          </div>
        )}
      </div>

      {/* Registering a type nobody sends yet leaves you with nothing to build
          against. This posts one through the SAME ingest path a real sender
          uses, so the shape it teaches is the shape you will actually get. */}
      <div className="border-outline flex items-center justify-between gap-4 border-t pt-4">
        <div className="min-w-0">
          <p className="text-ink text-xs font-semibold">Not ready to wire the sender up?</p>
          <p className="text-ink-subtle mt-0.5 text-xs">
            {tested
              ? 'Sent. The fields are read from it just like a real one.'
              : 'Send one yourself to shape the payload and start building.'}
          </p>
        </div>
        <Button
          variant="secondary"
          disabled={isBusy}
          onClick={() => {
            void onSendTest().then(() => setTested(true));
          }}
        >
          {isBusy ? 'Sending…' : 'Send a test event'}
        </Button>
      </div>
    </div>
  );
};
